import { createContext, type ReactNode, useContext } from "react";
import type { StravaConnectedServiceDto } from "../../../common/model/connectedServiceDto";
import { type UseStravaResult, useStrava } from "../components/hooks/useStrava";
import { ConnectStatus } from "../domain/ConnectStatus";
import type { ErrorInfo } from "../domain/ErrorInfo";

export interface ExternalServicesContextType {
  strava: {
    stopSyncAsync(): Promise<void>;
    startSyncAsync(continueFromHighWaterMark: boolean): Promise<boolean>;
    startConnect: () => void;
    finishConnectAsync: () => Promise<StravaConnectedServiceDto | null>;
    status: ConnectStatus;
    error: ErrorInfo | null;
  };
}

export const ExternalServicesContext: React.Context<ExternalServicesContextType> =
  createContext<ExternalServicesContextType>({
    strava: {
      startConnect: () => {},
      finishConnectAsync: async () => {
        console.warn("finishConnectAsync called before ExternalServicesContext was initialized.");
        return null;
      },
      status: ConnectStatus.NotChecked,
      startSyncAsync: async (_continueFromHighWaterMark) => {
        console.warn("startSyncAsync called before ExternalServicesContext was initialized.");
        return false;
      },
      stopSyncAsync: async () => {
        console.warn("stopSyncAsync called before ExternalServicesContext was initialized.");
      },
      error: null,
    },
  });

/**
 * @typedef ExternalServicesContextProviderProps
 * @property {ReactNode} children - React components that will be rendered as children of the ExternalServicesProvider.
 */
interface ExternalServicesContextProviderProps {
  children: ReactNode;
}

/**
 * ExternalServicesProvider is a React component that provides the ExternalServicesContext value to its children.
 * @param {ExternalServicesContextProviderProps} props - The properties for the ExternalServicesProvider component.
 */
export const ExternalServicesProvider: React.FC<ExternalServicesContextProviderProps> = ({
  children,
}) => {
  const strava: UseStravaResult = useStrava();

  return (
    <ExternalServicesContext.Provider value={{ strava }}>
      {children}
    </ExternalServicesContext.Provider>
  );
};

/**
 * Custom hook to access the ExternalServicesContext.
 * @returns {ExternalServicesContextType} The current ExternalServicesContext value.
 */
export const useExternalServices = (): ExternalServicesContextType =>
  useContext(ExternalServicesContext);
